<template>
  <Teleport to="body">
    <div v-if="showOverlay" class="som-overlay" role="dialog" aria-modal="true" aria-labelledby="som-title">
      <div class="som-panel card-background">
        <h2 id="som-title" class="som-brand text-center mb-3">Select-o-Matic</h2>

        <template v-if="step === 'prompt'">
          <p class="fw-bold text-center mb-4">
            Would you like to enter for a chance to compete in the buzzer bonus round?
          </p>
          <div class="som-prompt-actions d-flex flex-column gap-3">
            <button
              type="button"
              class="som-btn som-btn-yes"
              :disabled="busy"
              @click="onYes"
            >
              <span class="som-btn-icon" aria-hidden="true">🎴</span>
              <span class="som-btn-text">
                <span class="som-btn-label">Yes — deal my cards</span>
                <span class="som-btn-hint">Enter the draw for the buzzer bonus</span>
              </span>
            </button>
            <button
              type="button"
              class="som-btn som-btn-no"
              :disabled="busy"
              @click="onNo"
            >
              No thanks
            </button>
          </div>
        </template>

        <template v-else-if="step === 'cards' || step === 'revealed'">
          <p class="som-tagline text-center fw-bold mb-3">
            Choose wisely — the two teams with the highest cards are selected for the buzzer bonus!
          </p>

          <p class="small text-muted text-center mb-3">
            <strong>Suit tiebreak</strong> (strongest first): {{ suitLegend }}
          </p>

          <div class="som-cards-row">
            <div
              v-for="slot in [0, 1]"
              :key="slot"
              class="som-card-col"
            >
              <div
                class="som-card-3d"
                :class="{ disabled: !canTapPick() }"
                @click="onPickCard(slot)"
              >
                <div
                  class="som-card-inner"
                  :class="cardInnerClass(slot)"
                >
                  <div class="som-card-face som-card-back">
                    <img :src="cardBackSrc" alt="Card back" class="som-card-img" />
                  </div>
                  <div class="som-card-face som-card-front" :class="{ red: cardIdIsRed(cards[slot]) }">
                    <span class="som-face-rank">{{ rankPart(cards[slot]) }}</span>
                    <span class="som-face-suit">{{ symbolPart(cards[slot]) }}</span>
                  </div>
                </div>
              </div>
              <div v-if="labelForSlot(slot)" class="som-card-label text-center mt-2">
                <span v-if="labelForSlot(slot) === 'yours'" class="text-success fw-bold">
                  ✓ Your Card
                </span>
                <span v-else-if="labelForSlot(slot) === 'other'" class="text-danger fw-bold">
                  ✗ The card you didn’t pick
                </span>
              </div>
            </div>
          </div>

          <p v-if="dealError" class="text-danger small text-center mt-3">{{ dealError }}</p>

          <div v-if="step === 'revealed'" class="text-center mt-4">
            <p class="fw-bold mb-2">
              <template v-if="chosenCardDisplayName">
                You’re in the draw with {{ chosenCardDisplayName }}. Good luck!
              </template>
              <template v-else>
                You’re in the draw. Good luck!
              </template>
            </p>
            <button type="button" class="som-btn som-btn-yes som-btn-compact" @click="dismissToGame">
              Back to game
            </button>
          </div>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { ref, computed, watch, onUnmounted } from 'vue';
import { onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';
import { sessionDocRef, participantDocRef } from '@/services/selectOMaticService';
import {
  recordDeclined,
  dealParticipantCards,
  lockParticipantChoice,
} from '@/services/selectOMaticService';
import { SUIT_RANKING_DISPLAY, cardIdIsRed, RANK_SHORT, cardIdToShortLabel } from '@/utils/playingCards';
import cardBackImg from '@/assets/pys_cardback.png';

export default {
  name: 'SelectOMaticPlayer',
  props: {
    gameId: { type: String, required: true },
    teamId: { type: String, required: true },
    teamName: { type: String, default: '' },
  },
  setup(props) {
    const sessionActive = ref(false);
    const sessionId = ref('');
    const participant = ref(null);
    const busy = ref(false);
    const dealError = ref('');
    const step = ref('idle');
    const pickedSlot = ref(null);
    const revealPhase = ref('hidden');
    const animatingChoice = ref(false);
    const localDismiss = ref(false);
    let unsubSession = null;
    let unsubParticipant = null;

    const cardBackSrc = cardBackImg;

    const suitLegend = SUIT_RANKING_DISPLAY.join(', ');

    /** Decline applies only to the session it was recorded under. */
    const declinedCurrentSession = computed(
      () =>
        !!participant.value?.declined &&
        participant.value?.sessionId === sessionId.value &&
        !!sessionId.value
    );

    const showOverlay = computed(() => {
      if (!props.gameId || !props.teamId) return false;
      if (!sessionActive.value) return false;
      if (localDismiss.value) return false;
      if (declinedCurrentSession.value) return false;
      if (step.value === 'idle') return false;
      return true;
    });

    function syncStepFromParticipant() {
      if (animatingChoice.value) return;

      const p = participant.value;
      if (!sessionActive.value) {
        step.value = 'idle';
        return;
      }
      if (!p || p.sessionId !== sessionId.value) {
        step.value = 'prompt';
        pickedSlot.value = null;
        revealPhase.value = 'hidden';
        return;
      }
      if (p.declined) {
        step.value = 'idle';
        return;
      }
      if (Array.isArray(p.cards) && p.cards.length === 2) {
        if (p.chosenSlot != null && p.chosenCard != null) {
          step.value = 'revealed';
          pickedSlot.value = p.chosenSlot;
          revealPhase.value = 'both';
        } else {
          step.value = 'cards';
          revealPhase.value = 'hidden';
          pickedSlot.value = null;
        }
      } else {
        step.value = 'prompt';
      }
    }

    function attachListeners() {
      detachListeners();
      if (!props.gameId || !props.teamId) return;

      const sRef = sessionDocRef(db, props.gameId);
      unsubSession = onSnapshot(sRef, (snap) => {
        if (!snap.exists()) {
          sessionActive.value = false;
          sessionId.value = '';
          step.value = 'idle';
          return;
        }
        const d = snap.data();
        sessionActive.value = !!d.active;
        sessionId.value = d.sessionId || '';
        if (!sessionActive.value) {
          step.value = 'idle';
          return;
        }
        localDismiss.value = false;
        syncStepFromParticipant();
      });

      const pRef = participantDocRef(db, props.gameId, props.teamId);
      unsubParticipant = onSnapshot(pRef, (snap) => {
        const data = snap.exists() ? snap.data() : null;
        const sid = sessionId.value;
        const cur = participant.value;
        if (
          data &&
          sid &&
          data.sessionId !== sid &&
          cur &&
          cur.sessionId === sid &&
          Array.isArray(cur.cards) &&
          cur.cards.length === 2 &&
          cur.chosenCard == null &&
          cur.chosenSlot == null
        ) {
          return;
        }
        participant.value = data;
        if (sessionActive.value) syncStepFromParticipant();
      });
    }

    function detachListeners() {
      if (unsubSession) {
        unsubSession();
        unsubSession = null;
      }
      if (unsubParticipant) {
        unsubParticipant();
        unsubParticipant = null;
      }
    }

    watch(
      () => [props.gameId, props.teamId],
      () => {
        localDismiss.value = false;
        attachListeners();
      },
      { immediate: true }
    );

    watch(
      () => sessionId.value,
      () => {
        localDismiss.value = false;
      }
    );

    onUnmounted(() => detachListeners());

    const cards = computed(() => participant.value?.cards || []);

    function onNo() {
      dealError.value = '';
      if (!sessionId.value) return;
      busy.value = true;
      recordDeclined(db, props.gameId, props.teamId, props.teamName, sessionId.value)
        .catch((e) => {
          console.error(e);
          dealError.value = e.message || 'Could not record choice.';
        })
        .finally(() => {
          busy.value = false;
        });
    }

    async function onYes() {
      dealError.value = '';
      if (!sessionId.value) return;
      busy.value = true;
      try {
        const result = await dealParticipantCards(
          db,
          props.gameId,
          props.teamId,
          props.teamName,
          sessionId.value
        );
        const dealt = result?.cards;
        if (!Array.isArray(dealt) || dealt.length !== 2) {
          dealError.value = 'Could not deal cards.';
          return;
        }
        const sid = sessionId.value;
        participant.value = {
          sessionId: sid,
          teamId: props.teamId,
          teamName: props.teamName || 'Team',
          declined: false,
          cards: [...dealt],
          chosenCard: null,
          chosenSlot: null,
        };
        syncStepFromParticipant();
      } catch (e) {
        console.error(e);
        dealError.value = e.message || 'Could not deal cards.';
      } finally {
        busy.value = false;
      }
    }

    async function onPickCard(slot) {
      if (!canTapPick()) return;
      if (!sessionId.value || cards.value.length !== 2) return;

      busy.value = true;
      animatingChoice.value = true;
      dealError.value = '';
      try {
        await lockParticipantChoice(db, props.gameId, props.teamId, sessionId.value, slot);
        pickedSlot.value = slot;
        revealPhase.value = 'first';
        await new Promise((r) => setTimeout(r, 720));
        revealPhase.value = 'both';
        await new Promise((r) => setTimeout(r, 650));
        step.value = 'revealed';
      } catch (e) {
        console.error(e);
        dealError.value = e.message || 'Could not lock choice.';
      } finally {
        busy.value = false;
        animatingChoice.value = false;
      }
    }

    function canTapPick() {
      if (step.value !== 'cards' || busy.value || animatingChoice.value) return false;
      if (participant.value?.chosenSlot != null) return false;
      return true;
    }

    function dismissToGame() {
      localDismiss.value = true;
    }

    function cardInnerClass(slot) {
      const chosen = pickedSlot.value ?? participant.value?.chosenSlot;
      const isChosen = chosen === slot;
      const isOther = chosen != null && chosen !== slot;

      if (revealPhase.value === 'hidden') return {};
      if (revealPhase.value === 'first') {
        return { flipped: isChosen };
      }
      if (revealPhase.value === 'both') {
        return { flipped: isChosen || isOther };
      }
      return {};
    }

    function labelForSlot(slot) {
      const chosen = pickedSlot.value ?? participant.value?.chosenSlot;
      if (chosen == null || revealPhase.value === 'hidden') return null;
      if (revealPhase.value === 'first' && slot !== chosen) return null;
      if (slot === chosen) return 'yours';
      if (revealPhase.value === 'both' && slot !== chosen) return 'other';
      return null;
    }

    function rankPart(id) {
      if (id == null) return '';
      return RANK_SHORT[id % 13];
    }

    function symbolPart(id) {
      if (id == null) return '';
      const s = Math.floor(id / 13);
      return ['♣', '♦', '♥', '♠'][s];
    }

    const chosenCardDisplayName = computed(() => {
      const fromDoc = participant.value?.chosenCard;
      const slot = pickedSlot.value ?? participant.value?.chosenSlot;
      const fromCards =
        slot != null && Array.isArray(cards.value) ? cards.value[slot] : null;
      const id = fromDoc != null ? fromDoc : fromCards;
      return id != null && id !== '' ? cardIdToShortLabel(Number(id)) : '';
    });

    return {
      showOverlay,
      sessionActive,
      sessionId,
      step,
      busy,
      dealError,
      onYes,
      onNo,
      onPickCard,
      cards,
      cardBackSrc,
      suitLegend,
      cardInnerClass,
      labelForSlot,
      rankPart,
      symbolPart,
      cardIdIsRed,
      pickedSlot,
      revealPhase,
      canTapPick,
      dismissToGame,
      chosenCardDisplayName,
    };
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Lilita+One&display=swap');

.card-background {
  background: linear-gradient(145deg, #ddd6fe, #ede9fe);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  border-radius: 25px !important;
  padding: 1.25rem;
  width: 100%;
}

.som-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(45, 16, 101, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overflow-y: auto;
}

.som-panel {
  max-width: 440px;
  width: 100%;
}

.som-brand {
  font-family: 'Lilita One', sans-serif;
  color: #4c1d95;
  letter-spacing: 0.04em;
}

.som-tagline {
  font-size: 0.95rem;
  line-height: 1.35;
}

.som-cards-row {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.som-card-col {
  flex: 1;
  min-width: 120px;
  max-width: 160px;
}

.som-card-3d {
  perspective: 900px;
  cursor: pointer;
  min-height: 168px;
}

.som-card-3d.disabled {
  cursor: default;
  opacity: 0.95;
}

.som-card-inner {
  position: relative;
  width: 100%;
  padding-top: 140%;
  transform-style: preserve-3d;
  transition: transform 0.65s ease;
}

.som-card-inner.flipped {
  transform: rotateY(180deg);
}

.som-card-face {
  position: absolute;
  inset: 0;
  border-radius: 12px;
  backface-visibility: hidden;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.som-card-back {
  background: #2e1065;
}

.som-card-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
}

.som-card-front {
  transform: rotateY(180deg);
  background: linear-gradient(160deg, #fff 0%, #f3e8ff 100%);
  border: 2px solid #7c3aed;
  color: #1e1b4b;
}

.som-card-front.red {
  color: #b91c1c;
}

.som-face-rank {
  font-size: 1.75rem;
  font-weight: 800;
  line-height: 1;
}

.som-face-suit {
  font-size: 2.5rem;
  line-height: 1;
  margin-top: 0.25rem;
}

.som-card-label {
  min-height: 2.5rem;
  font-size: 0.85rem;
}

.som-prompt-actions {
  max-width: 100%;
}

.som-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 3.25rem;
  padding: 0.85rem 1.25rem;
  border-radius: 14px;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.25;
  border: 2px solid transparent;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.2s ease, filter 0.15s ease;
  box-shadow: 0 4px 0 rgba(45, 16, 101, 0.35), 0 6px 14px rgba(76, 29, 149, 0.25);
}

.som-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.som-btn:not(:disabled):active {
  transform: translateY(2px);
  box-shadow: 0 2px 0 rgba(45, 16, 101, 0.3), 0 3px 8px rgba(76, 29, 149, 0.2);
}

.som-btn-yes {
  flex-direction: row;
  gap: 0.75rem;
  text-align: left;
  color: #fff;
  background: linear-gradient(180deg, #9333ea 0%, #6d28d9 55%, #5b21b6 100%);
  border-color: rgba(255, 255, 255, 0.45);
}

.som-btn-yes:not(:disabled):hover {
  filter: brightness(1.06);
}

.som-btn-icon {
  font-size: 1.75rem;
  line-height: 1;
  flex-shrink: 0;
}

.som-btn-text {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.som-btn-label {
  display: block;
}

.som-btn-hint {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  opacity: 0.92;
}

.som-btn-compact {
  min-height: 2.75rem;
  padding: 0.65rem 1.25rem;
  flex-direction: row;
  text-align: center;
}

.som-btn-compact .som-btn-text {
  flex-direction: row;
  gap: 0;
}

.som-btn-compact .som-btn-hint {
  display: none;
}

.som-btn-compact .som-btn-icon {
  display: none;
}

.som-btn-no {
  color: #3b0764;
  background: #f5f3ff;
  border: 2px solid #a78bfa;
  box-shadow: 0 2px 0 #7c3aed, 0 4px 10px rgba(124, 58, 237, 0.15);
}

.som-btn-no:not(:disabled):hover {
  background: #ede9fe;
}
</style>
